import { useFieldArray, Control } from "react-hook-form";
import { SchemaField } from "@/types/schemaBuilder";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Plus, Trash2 } from "lucide-react";

interface ArrayFieldRendererProps {
  control: Control<any>;
  fullPath: string;
  field: SchemaField;
  label: string;
  description?: string;
  renderSchemaField: (
    fieldName: string,
    field: SchemaField,
    parentPath: string
  ) => React.ReactNode;
}

export function ArrayFieldRenderer({
  control,
  fullPath,
  field,
  label,
  description,
  renderSchemaField,
}: ArrayFieldRendererProps) {
  const { fields, append, remove } = useFieldArray({
    control,
    name: fullPath,
  });

  if (!field.items) return null;

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle className="text-base">{label}</CardTitle>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        {fields.map((item, index) => (
          <div key={item.id} className="relative border rounded-lg p-4 pt-8">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="absolute right-2 top-2"
              onClick={() => remove(index)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>

            {field.items.type === "object" && field.items.properties ? (
              // Render object array item
              <div className="space-y-4">
                {Object.entries(field.items.properties).map(([propName, propField]) => (
                  <div key={propName}>
                    {renderSchemaField(
                      propName,
                      propField,
                      `${fullPath}.${index}`
                    )}
                  </div>
                ))}
              </div>
            ) : (
              // Render simple array item
              <FormField
                control={control}
                name={`${fullPath}.${index}`}
                rules={{ required: field.required }}
                render={({ field: formField }) => (
                  <FormItem>
                    <FormLabel>Item {index + 1}</FormLabel>
                    <FormControl>
                      {field.items.type === "string" ? (
                        field.items.multiline ? (
                          <Textarea {...formField} />
                        ) : (
                          <Input {...formField} />
                        )
                      ) : field.items.type === "number" ? (
                        <Input
                          type="number"
                          {...formField}
                          onChange={(e) =>
                            formField.onChange(e.target.valueAsNumber)
                          }
                        />
                      ) : field.items.type === "boolean" ? (
                        <Checkbox
                          checked={formField.value}
                          onCheckedChange={formField.onChange}
                        />
                      ) : null}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}
          </div>
        ))}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => {
            const defaultValue = field.items.type === "boolean" ? false :
              field.items.type === "number" ? 0 :
              field.items.type === "object" ? {} : "";
            append(defaultValue);
          }}
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </CardContent>
    </Card>
  );
}